<template>
  <PatientFilterChip
    v-for="hospital in vitalSignsFilters.hospitals"
    :key="hospital.id"
    :text="hospital.name"
    color="primary"
    :data-testid="`hospital-chip-${hospital.id}`"
    @click:close="removeHospitalFilter(hospital.id)"
  />
  <PatientFilterChip
    v-for="department in vitalSignsFilters.departments"
    :key="department.id"
    :text="t(department.name)"
    color="secondary"
    :data-testid="`department-chip-${department.id}`"
    @click:close="removeDepartmentFilter(department.id)"
  />
  <PatientFilterChip
    v-for="ward in vitalSignsFilters.wards"
    :key="ward.id"
    :text="ward.name"
    color="secondary"
    :data-testid="`ward-chip-${ward.id}`"
    @click:close="removeWardFilter(ward.id)"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.temperatureRange.length"
    :text="`${getParameterLabel(Parameter.Temperature)}: ${formatRange(vitalSignsFilters.temperatureRange)}`"
    color="ternary"
    data-testid="temperature-range-chip"
    @click:close="removeRangeFilter('temperatureRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.lactateRange.length"
    :text="`${getParameterLabel(Parameter.Lactate)}: ${formatRange(vitalSignsFilters.lactateRange)}`"
    color="ternary"
    data-testid="lactate-range-chip"
    @click:close="removeRangeFilter('lactateRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.pHRange.length"
    :text="`${getParameterLabel(Parameter.PH)}: ${formatRange(vitalSignsFilters.pHRange)}`"
    color="ternary"
    data-testid="pH-range-chip"
    @click:close="removeRangeFilter('pHRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.potassiumRange.length"
    :text="`${getParameterLabel(Parameter.Potassium)}: ${formatRange(vitalSignsFilters.potassiumRange)}`"
    color="ternary"
    data-testid="potassium-range-chip"
    @click:close="removeRangeFilter('potassiumRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.hemoglobinRange.length"
    :text="`${getParameterLabel(Parameter.Hemoglobin)}: ${formatRange(vitalSignsFilters.hemoglobinRange)}`"
    color="ternary"
    data-testid="hemoglobin-range-chip"
    @click:close="removeRangeFilter('hemoglobinRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.heartRateRange.length"
    :text="`${getParameterLabel(Parameter.HeartRate)}: ${formatRange(vitalSignsFilters.heartRateRange)}`"
    color="ternary"
    data-testid="heart-rate-range-chip"
    @click:close="removeRangeFilter('heartRateRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.oxygenSaturationRange.length"
    :text="`${getParameterLabel(Parameter.OxygenSaturation)}: ${formatRange(vitalSignsFilters.oxygenSaturationRange)}`"
    color="ternary"
    data-testid="oxygen-saturation-range-chip"
    @click:close="removeRangeFilter('oxygenSaturationRange')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.vasopressors"
    :text="t('Vasopressors')"
    color="ternary"
    data-testid="vasopressors-chip"
    @click:close="removeRangeFilter('vasopressors')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.dialysis"
    :text="t('Dialysis')"
    color="ternary"
    data-testid="dialysis-chip"
    @click:close="removeRangeFilter('dialysis')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.ventilation"
    :text="t('Ventilation')"
    color="ternary"
    data-testid="ventilation-chip"
    @click:close="removeRangeFilter('ventilation')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.ecmo"
    :text="t('ECMO')"
    color="ternary"
    data-testid="ecmo-chip"
    @click:close="removeRangeFilter('ecmo')"
  />
  <PatientFilterChip
    v-if="vitalSignsFilters.impella"
    :text="t('Impella')"
    color="ternary"
    data-testid="impella-chip"
    @click:close="removeRangeFilter('impella')"
  />
</template>

<script setup lang="ts">
import PatientFilterChip from '@/components/patient/filter/PatientFilterChip.vue'
import { storeToRefs } from 'pinia'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'
import { useI18n } from 'vue-i18n'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'

const { t } = useI18n()
const { getParameterLabel } = useParameterLabel()

const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const { vitalSignsFilters } = storeToRefs(patientsWithVitalSignsStore)

const { removeDepartmentFilter, removeWardFilter, removeHospitalFilter, removeRangeFilter } =
  patientsWithVitalSignsStore

function formatRange(range: readonly (string | number)[]): string {
  return `${range[0]} - ${range[1]}`
}
</script>
